<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { ref, useTemplateRef } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';

import FallbackMdiIconCmn from '@/components/cmns/FallbackMdiIconCmn.vue';
import KTextField from '@/components/fields/KTextField.vue';
import useField from '@/composables/useField';
import usePickerField from '@/composables/usePickerField';

import datepickerStyles from '@/styles/datepicker.module.scss';

export interface ModelValue {
  hours: number;
  minutes: number;
  seconds?: number;
}

export interface Props {
  modelValue?: ModelValue | null;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  hideDetails?: boolean;
  rules?: object;
  size?: FieldSize;
  autoApply?: boolean;
  startTime?: ModelValue;
}

export type TimePickerFieldProps = Props;

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: 'Time',
  loading: false,
  disabled: false,
  readonly: false,
  hideDetails: false,
  rules: () => {
    return [];
  },
  size: undefined,
  autoApply: false,
  startTime: undefined,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const datepickerEl = useTemplateRef('datepicker');
const root = ref();

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});

const {
  pickerAttrs,
  handleAllEnterKeydown,
  handleFocus,
  handleClick,
  handleAllEscKeydown,
  handleAppendIconClick,
  handleAllSpaceKeydown,
  handleDownKeydown,
} = usePickerField(root, datepickerEl, () => props.readonly);
</script>

<template>
  <div ref="root" data-testid="timePickerField">
    <VueDatePicker
      v-bind="pickerAttrs"
      ref="datepicker"
      v-model="fieldValue"
      :auto-apply="props.autoApply"
      :class="[
        $style.timePickerField,
        {
          [$style.datepickerHideDetails]: props.hideDetails,
        },
      ]"
      :ui="{
        menu: datepickerStyles.dpCustomMenu,
        calendar: datepickerStyles.dpCustomCalendar,
      }"
      :time-config="{ is24: false, startTime: props.startTime }"
      :text-input="{
        enterSubmit: true,
        tabSubmit: true,
        openMenu: !(props.disabled || props.readonly) ? 'open' : false,
      }"
      time-picker
      @keydown.enter="handleAllEnterKeydown"
      @keydown.space="handleAllSpaceKeydown"
      @keydown.esc="handleAllEscKeydown"
    >
      <template #arrow-left>
        <FallbackMdiIconCmn icon="mdi-chevron-left" />
      </template>
      <template #arrow-right>
        <FallbackMdiIconCmn icon="mdi-chevron-right" />
      </template>
      <template #arrow-up>
        <FallbackMdiIconCmn icon="mdi-chevron-up" />
      </template>
      <template #arrow-down>
        <FallbackMdiIconCmn icon="mdi-chevron-down" />
      </template>
      <template #clock-icon>
        <FallbackMdiIconCmn icon="mdi-clock-outline" />
      </template>
      <template
        #dp-input="{
          value,
          onInput,
          onEnter,
          onTab,
          onClear,
          onBlur,
          onKeypress,
          onPaste,
          isMenuOpen,
        }"
      >
        <KTextField
          append-inner-icon="mdi-clock-outline"
          :label="props.label"
          :clearable="!props.readonly"
          :model-value="value"
          :loading="props.loading"
          :disabled="props.disabled"
          :readonly="props.readonly"
          :hide-details="props.hideDetails"
          :rules="props.rules"
          :size="props.size"
          :class="$style.field"
          @input="onInput"
          @keydown.enter="onEnter"
          @keydown.tab="onTab"
          @blur="onBlur"
          @keypress="onKeypress"
          @keydown.down.prevent="handleDownKeydown($event, isMenuOpen)"
          @paste="onPaste"
          @focus="handleFocus($event, isMenuOpen)"
          @click="handleClick($event, isMenuOpen)"
          @click:clear="onClear"
          @click:append-inner="handleAppendIconClick($event, isMenuOpen)"
        />
      </template>
    </VueDatePicker>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.timePickerField {
  padding-bottom: 22px;
}
.datepickerHideDetails {
  padding-bottom: 0;
}

.field {
  :global {
    input.v-field__input {
      padding-right: 0;
    }
    .v-input__details {
      position: absolute;
      bottom: -22px;
    }
  }
}
</style>
